import { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Per-row unit-type selection: { selections: Map<rowId, unitType>, setUnitType(rowId, value) }.
export const UnitTypeContext = createContext(null);

export const MIXED_UNIT_TYPE = 'mixed';

export const useRowUnitType = (rowId, fallback) => {
  const ctx = useContext(UnitTypeContext);
  return {
    unitType: ctx?.selections.get(rowId) ?? fallback,
    setUnitType: ctx?.setUnitType,
  };
};

export const resolveRowUnitType = (row, selections) => selections.get(row.id) ?? row.unitType;

// MIXED_UNIT_TYPE unless every row is showing the same unit.
export const resolveHeaderUnitType = (rows, selections) => {
  if (rows.length === 0) return MIXED_UNIT_TYPE;
  const first = resolveRowUnitType(rows[0], selections);
  return rows.every((row) => resolveRowUnitType(row, selections) === first)
    ? first
    : MIXED_UNIT_TYPE;
};

/** Owns the per-row selection and the header's set-every-row action. */
export const useUnitTypeSelection = () => {
  const [selections, setSelections] = useState(() => new Map());

  const setUnitType = useCallback((rowId, value) => {
    setSelections((prev) => {
      const next = new Map(prev);
      next.set(rowId, value);
      return next;
    });
  }, []);

  const setAllUnitTypes = useCallback((rowIds, value) => {
    setSelections(new Map(rowIds.map((rowId) => [rowId, value])));
  }, []);

  const contextValue = useMemo(() => ({ selections, setUnitType }), [selections, setUnitType]);

  return { selections, setAllUnitTypes, contextValue };
};

/**
 * Everything a list page needs to wire unit switching: the context value for its
 * cells, and the column's `headerSelect` descriptor.
 *
 * @param rows        the current page of rows (each needs `id` + `unitType`)
 * @param buildOptions the slice's buildUnitHeaderOptions
 * @param t            useTranslations()
 */
export const useUnitTypeHeader = (rows, buildOptions, t) => {
  const { selections, setAllUnitTypes, contextValue } = useUnitTypeSelection();

  const value = useMemo(() => resolveHeaderUnitType(rows, selections), [rows, selections]);

  const onChange = useCallback(
    (event) =>
      setAllUnitTypes(
        rows.map((row) => row.id),
        event.target.value,
      ),
    [setAllUnitTypes, rows],
  );

  const unitHeaderSelect = useMemo(
    () => ({
      value,
      options: buildOptions(t, value),
      onChange,
      ariaLabel: t('UNIT_TYPE_ALL_ARIA'),
    }),
    [value, buildOptions, onChange, t],
  );

  // `selections` is returned so a screen can resolve a row's unit outside a cell (modals).
  return { unitHeaderSelect, contextValue, selections };
};
